import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { PiPlugsConnectedFill } from "react-icons/pi";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import apiClient from "../services/apiClient";

import { removeSipConfig, updateSipConfig } from "../features/SIP/SIPConfigSlice";
import { removeDefaultExtension, updateUserDetails } from "../features/user/UserSlice";
import PageLoader from "../components/Loading/PageLoader";
import CustomInputBox from "../components/Form/CustomInputBox";

const UserExtensions = () => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);

  const userInfo = useSelector((state) => state?.userInfo?.user);
  const SIPConfig = useSelector((state) => state.SIPConfig);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchUserExtensions();
  }, []);

  const fetchUserExtensions = async () => {
    try {
      const response = await apiClient.get(
        `assignedPhoneDetails?company_id=${userInfo?.company_id}&user_id=${userInfo?.id}`
      );

      const transformedData = response?.data?.map((item) => ({
        username: item?.data.username,
        caller_id: item?.data.effective_caller_id_number,
        password: item?.data.password,
        credentials: item?.data,
        _id: item?._id,
      }));

      setExtensions(transformedData);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultExtension = async (sipSettings) => {
    setLoading(true);

    try {
      const settings = {
        default_extension: {
          extension: sipSettings?.username,
          password: sipSettings?.password,
          caller_id: sipSettings?.caller_id,
        },
      };

      const payload = new FormData();
      payload.append("user_id", userInfo?.id);
      payload.append("settings", JSON.stringify(settings));

      await apiClient.put("/user/update", payload);

      dispatch(
        updateSipConfig({
          sipUsername: sipSettings?.username,
          sipPassword: sipSettings?.password,
          sipServer: userInfo?.company_domain,
        })
      );

      dispatch(updateUserDetails({ settings }));

      toast.success("Default Extension set!");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDefaultExtension = async (sipSettings) => {
    setLoading(true);

    try {
      const settings = {
        default_extension: {
          extension: "",
          password: "",
          caller_id: "",
        },
      };

      const payload = new FormData();
      payload.append("user_id", userInfo?.id);
      payload.append("settings", JSON.stringify(settings));

      await apiClient.put("/user/update", payload);

      dispatch(removeSipConfig());
      dispatch(removeDefaultExtension());

      toast.success("Default Extension Removed!");
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "username", header: "Extension" },
      { accessorKey: "password", header: "password" },
      { accessorKey: "caller_id", header: "Number" },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: extensions,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton
          color={SIPConfig?.sipUsername === row?.original?.credentials?.username ? "primary" : ""}
          onClick={() => {
            SIPConfig?.sipUsername === row?.original?.credentials?.username
              ? handleRemoveDefaultExtension()
              : handleSetDefaultExtension(row?.original);
          }}
          title={
            SIPConfig?.sipUsername === row?.original?.credentials?.username
              ? "default extension connected"
              : "set default extension"
          }
        >
          <PiPlugsConnectedFill />
        </IconButton>
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      {loading && <PageLoader text="Loading Numbers" />}

      <>
        <div className="pageTitle1 p-5">
          <h3>Allocated Number and Extensions</h3>
          <p>{extensions?.length} Available</p>
        </div>

        <Box sx={{ padding: 2 }}>
          <div className="d-flex justify-content-between">
            <div className="w-50">
              <CustomInputBox
                icon={SearchIcon}
                placeholder="Search Number or Extension"
                type="text"
                backgroundColor={"#FFFFFF"}
                borderRadius="8px"
              />
            </div>

            <div className="d-flex justify-content-end gap-3 mb-4"></div>
          </div>

          <MaterialReactTable table={table} />
        </Box>
      </>
    </>
  );
};

export default UserExtensions;
